import { useState, useEffect, useMemo } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/hooks/useCanonical";
import { buildCategorySeo } from "@/lib/seo";
import { buildCategoryJsonLd } from "@/lib/structuredData";

interface Job {
  id: string;
  category_id: string;
  title: string;
  slug: string;
}

interface Image {
  id: string;
  job_id: string;
  url: string;
}

const Work = () => {
  const { category } = useParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [images, setImages] = useState<Image[]>([]);
  const [categoryData, setCategoryData] = useState<{ id: string; name: string } | null>(null);

  const jsonLd = useMemo(() => {
    return buildCategoryJsonLd({
      categorySlug: category,
      categoryName: categoryData?.name || category,
      items: jobs.map((job) => ({
        url: `https://www.inanederdal.se/work/${category}/${job.slug}`,
        name: job.title,
        image: images.find((img) => img.job_id === job.id)?.url,
      })),
    });
  }, [jobs, category, categoryData, images]);

  const categoryImage = jobs.map((job) => images.find((img) => img.job_id === job.id)?.url).find(Boolean);
  const seo = buildCategorySeo({
    categorySlug: category,
    categoryName: categoryData?.name || category,
    image: categoryImage,
  });

  useSEO({ ...seo, jsonLd });

  useEffect(() => {
    const loadData = async () => {
      // Get category by slug
      const { data: cat } = await supabase
        .from("categories")
        .select("id, name")
        .eq("slug", category || "")
        .single();

      if (cat) {
        setCategoryData(cat);

        // Get jobs for this category
        const { data: jobsData } = await supabase
          .from("jobs")
          .select("*")
          .eq("category_id", cat.id)
          .order("sort_order");

        if (jobsData) {
          const filteredJobs = (category === "wedding")
            ? jobsData.filter(job => job.slug !== "wedding")
            : jobsData;

          setJobs(filteredJobs);

          // Get only the first image for each job efficiently using RPC
          const jobIds = filteredJobs.map(j => j.id);
          const { data: imgsData } = await supabase
            .rpc("get_first_images_for_jobs", { job_ids: jobIds });

          if (imgsData) setImages(imgsData);
        }
      }
    };

    loadData();
  }, [category]);

  const getFirstImage = (jobId: string) => {
    return images.find(img => img.job_id === jobId)?.url || "";
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="pt-32 pb-16 flex-1">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-12">
            {jobs.map((job) => (
              <Link 
                key={job.id}
                to={`/work/${category}/${job.slug}`}
                className="group"
              >
                <div className="aspect-square overflow-hidden mb-4">
                  <img 
                    src={getFirstImage(job.id)} 
                    alt={job.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="relative text-foreground tracking-wide transition-colors inline-block">
                  {job.title}
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-accent scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </p>
              </Link>
            ))}
          </div>

          {category === "wedding" && (
            <section className="mt-12 md:mt-16 text-center max-w-2xl mx-auto">
              <p className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-5">Bröllopsfotograf i Stockholm</p>
              <h2 className="text-3xl md:text-4xl font-small-heading font-extrabold italic tracking-tight mb-6">
                Planerar ni ert bröllop?
              </h2>
              <p className="text-lg leading-relaxed text-foreground/85 mb-10">
                Läs mer om hur jag fotograferar bröllop, vad som ingår och vad det kostar. Hör sedan av er så
                planerar vi dagen tillsammans.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-red-500 text-white hover:bg-red-600 px-8 py-6 text-base font-medium"
              >
                <Link to="/brollopsfotograf">Läs mer om bröllopsfotografering</Link>
              </Button>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Work;
